<script lang="ts">
  import { onDestroy, untrack } from "svelte";
  import { api, type AdminInventoryQuery } from "@/api";
  import { token } from "@/stores/session";
  import Button from "./Button.svelte";
  let {
    filters = {},
    value = $bindable(""),
    selectedLabel = "",
    label,
    emptyLabel = "Выберите вариант",
    disabled = false,
    required = false,
  }: {
    filters?: AdminInventoryQuery;
    value?: string;
    selectedLabel?: string;
    label: string;
    emptyLabel?: string;
    disabled?: boolean;
    required?: boolean;
  } = $props();
  let options = $state<Array<{ value: string; label: string }>>([]);
  let retained = $state<{ value: string; label: string } | null>(null);
  let active = $state(false);
  let query = $state("");
  let loading = $state(false);
  let error = $state<string | null>(null);
  let nextCursor = $state<string | null>(null);
  let page = $state(1);
  let failedTarget = 1;
  let cursors: Array<string | null> = [null];
  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  async function load(target = 1) {
    if (!$token) {
      return;
    }
    controller?.abort();
    const request = new AbortController();
    controller = request;
    const cursor = target === 1 ? null : target > page ? nextCursor : cursors[target - 1];
    failedTarget = target;
    loading = true;
    error = null;
    try {
      const input = { ...filters, query, cursor, limit: 10 };
      const data = await api.adminNodes($token, input, request.signal);
      const result = {
        ...data,
        items: data.items.map((node) => ({
          value: node.id,
          label: `${node.name} (${node.status})`,
        })),
      };
      if (request.signal.aborted) {
        return;
      }
      options = result.items;
      nextCursor = result.nextCursor;
      cursors = target === 1 ? [null] : [...cursors.slice(0, target - 1), cursor];
      page = target;
    } catch (cause) {
      if (!request.signal.aborted) {
        error = cause instanceof Error ? cause.message : "Не удалось загрузить варианты";
      }
    } finally {
      if (!request.signal.aborted) {
        loading = false;
      }
    }
  }
  function search() {
    clearTimeout(timer);
    active = true;
    controller?.abort();
    options = [];
    nextCursor = null;
    page = 1;
    cursors = [null];
    timer = setTimeout(() => void load(1), 300);
  }
  function activate() {
    if (!active) {
      active = true;
      void load(1);
    }
  }
  $effect(() => {
    JSON.stringify(filters);
    void $token;
    untrack(() => {
      controller?.abort();
      options = [];
      nextCursor = null;
      page = 1;
      cursors = [null];
      if (active) {
        void load(1);
      }
    });
  });
  onDestroy(() => {
    controller?.abort();
    clearTimeout(timer);
  });
</script>
<div class="inventory-selector">
  <input
    aria-label={`Поиск: ${label}`}
    placeholder="Поиск вариантов"
    bind:value={query}
    oninput={search}
    onfocus={activate}
    {disabled}
  />
  <select
    aria-label={label}
    bind:value
    onfocus={activate}
    {disabled}
    {required}
    onchange={(event) => {
      retained = options.find((option) => option.value === event.currentTarget.value) ?? retained;
    }}
  >
    <option value="">{emptyLabel}</option>
    {#if value && !options.some((option) => option.value === value)}<option {value}
        >{retained?.value === value ? retained.label : selectedLabel || value}</option
      >{/if}
    {#each options as option (option.value)}<option value={option.value}>{option.label}</option
      >{/each}
  </select>
  {#if loading}<small role="status">Загружаем варианты…</small>{/if}
  {#if error}<div role="alert">
      {error}
      <Button variant="ghost" disabled={loading} onclick={() => void load(failedTarget)}
        >Повторить варианты</Button
      >
    </div>{/if}
  {#if active}<div class="pagination">
      <Button
        variant="ghost"
        size="compact"
        disabled={disabled || loading || page === 1}
        onclick={() => void load(page - 1)}>Назад</Button
      ><small>Варианты · страница {page}</small><Button
        variant="ghost"
        size="compact"
        disabled={disabled || loading || !nextCursor}
        onclick={() => void load(page + 1)}>Далее</Button
      >
    </div>{/if}
</div>
<style>
  .inventory-selector {
    display: grid;
    gap: 0.5rem;
    min-width: 0;
  }
  input,
  select {
    box-sizing: border-box;
    min-width: 0;
    max-width: 100%;
    width: 100%;
    padding: 0.65rem;
    background: var(--surface);
    color: var(--cream);
    border: 1px solid var(--line-strong);
  }
  .pagination {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }
  small {
    color: var(--muted);
  }
</style>
